/**
 * Reducer to manage recipients and contacts related states
 */

import { ActionTypes as types, MAX_RECIPIENTS } from '../constants'
import serverProps from '../utils/serverProps'
import { isPhone, isEmail } from '../../../../shared/validators'
import { findRecipientInContacts } from '../../lib/contact-utils'
import { removeMailto } from '../utils/remove-mailto'
import _ from 'lodash'

function getInitialState(props = serverProps) {
  const {
    recipientType = '',
    recipientData: { recipients = [], recipientList = [] } = {},
  } = props
  return {
    contacts: [],
    topContacts: [],
    otherContacts: [],
    searchedContacts: [],
    hasFetchedContacts: false,
    /*
     * recipients array === [{
     *     recipient: String,
     *     thumbnailUrl: String
     *     firstInitial: String,
     *     lastInitial: String,
     *     displayName: String,
     *    contact: null or Object like above
     * }, ...]
     */
    recipients,
    recipientList,
    currentRecipient: '',
    currentRecipientDisplayName: '',
    selectedRecipientIndex: -1,
    isCurrentRecipientValid: false,
    country: '',
    recipientType,
  }
}

const initialState = getInitialState(serverProps)

// Fill contacts with received information.
function receivedContacts(state, action) {
  const topContacts = [...action.data.topContacts]
  const otherContacts = [...action.data.otherContacts]

  // Remove contacts that are already in recipients.
  state.recipients.forEach(recipient => {
    const rcontact = recipient.contact
    if (!rcontact) {
      return
    }

    let index = topContacts.findIndex(contact => contact.id === rcontact.id)
    if (index >= 0) {
      return topContacts.splice(index, 1)
    }
    index = otherContacts.findIndex(contact => contact.id === rcontact.id)
    if (index >= 0) {
      return otherContacts.splice(index, 1)
    }
  })

  return {
    ...state,
    contacts: [...topContacts, ...otherContacts],
    topContacts,
    otherContacts,
    hasFetchedContacts: true,
  }
}

function receivedSearchedContacts(state, action) {
  // lets remove contacts that have already been selected into the recipient list
  const rcontacts = state.recipients.map(recipient => recipient.contact)

  // logic copied from contacts reducer
  const searchedContacts = _.differenceBy(
    _.unionBy(state.searchedContacts, action.data.contacts, 'id'),
    [...state.contacts, ...rcontacts],
    'id'
  )

  return {
    ...state,
    searchedContacts,
  }
}

// Update current recipient display name.
function changeCurrentRecipientDisplayName(state, action) {
  return {
    ...state,
    currentRecipientDisplayName: action.data.displayName,
    selectedRecipientIndex: -1,
  }
}

// Update current recipient.
function changeCurrentRecipient(state, action) {
  // Do nothing when the user starts typing when the maximum number of selected recipients is reached.
  if (state.recipients.length >= MAX_RECIPIENTS) {
    return state
  }

  return {
    ...state,
    currentRecipient: action.data.currentRecipient,
    isCurrentRecipientValid:
      isEmail(action.data.currentRecipient) ||
      isPhone(action.data.currentRecipient),
    selectedRecipientIndex: -1,
  }
}

// Update selected recipient index.
function changeSelectedRecipientIndex(state, action) {
  // Do nothing if there is a typed-out recipient or there are no selected recipients.
  if (state.currentRecipient || !state.recipients.length) {
    return {
      ...state,
    }
  }

  let index = action.data.index

  // Wrap index around.
  if (index < 0) {
    index = state.recipients.length - 1
  } else if (index >= state.recipients.length) {
    index = 0
  }

  return {
    ...state,
    selectedRecipientIndex: index,
  }
}

// Moves selected contact to recipients.
function selectContact(state, action) { // NOSONAR
  // Nothing should happen when the user selected a contact when the maximum
  // number of selected recipients is reached.
  if (state.recipients.length >= MAX_RECIPIENTS) {
    return state
  }

  const {
    index: selectedContactIndex,
    email: selectedContactEmail,
  } = action.data.contact

  // If the "contact" isn't really a contact, try to find it in
  // contacts. If we can't find it, continue as unilateral. Otherwise,
  // use the found contact.
  let foundContact = action.data.contact
  foundContact = removeMailto(foundContact) // remove 'mailto:' from email

  if (selectedContactIndex === -1) {
    foundContact =
      findRecipientInContacts(selectedContactEmail, state.contacts) ||
      foundContact
  }

  // Pull out profile information from found contact
  const {
    fullName,
    firstName,
    lastName,
    alternateFullName = '',
    displayName,
    thumbnailUrl = '',
    email,
    phones = [],
    id,
    payerId = '',
    hasNetworkId = false,
    networkIdDisplayName = '',
    networkId = '',
  } = foundContact

  let firstInitial = ''
  let lastInitial = ''

  if (firstName && lastName) {
    // Build out the initials if fullName exists.
    firstInitial = firstName.charAt(0) || alternateFullName.charAt(0)
    lastInitial = lastName.charAt(0)
  }

  // Pull out the first phone number.
  let [phone = {}] = phones

  // Pull out the phone_number property, which will be either object or string.
  const { phone_number = {} } = phone

  // Try to pull out the properties.
  const { country_code = '', national_number = '' } = phone_number

  // If the properties exist, let's construct the string, else `phone_number`
  // is a string, so just use that.
  if (country_code && national_number) {
    phone = `+${country_code} ${national_number}`
  } else {
    phone = ''
  }

  // Check if the recipient is already selected.
  // Recipient and phone are both empty strings with peerModel for directory search
  if (
    state.recipients.some(
      recipient =>
        recipient.recipient &&
        (recipient.recipient === email ||
          (phone && recipient.recipient === phone))
    )
  ) {
    return state
  }

  const networkIdInfo = {
    recipientDisplayDetails: email || phone,
  }
  if (hasNetworkId) {
    networkIdInfo.recipientDisplayDetails = networkIdDisplayName
    networkIdInfo.networkId = networkId
  }
  // Append our new recipient.
  const recipients = [
    ...state.recipients,
    {
      recipient: email || phone,
      payerId,
      recipientDisplayDetails: email || phone,
      displayName:
        (fullName && fullName.trim()) || displayName || alternateFullName || '',
      firstName,
      lastName,
      alternateFullName,
      fullName,
      firstInitial,
      lastInitial,
      thumbnailUrl,
      contact: id ? foundContact : null,
      ...networkIdInfo,
    },
  ]

  const recipientList = state.recipientList.concat([
    {
      recipient: email || phone,
      payerId,
      value: '',
      amount: {
        multi: '',
        split: '',
      },
      showInvalid: false,
      isAmountFrozen: false,
      ...networkIdInfo,
    },
  ])

  return {
    ...state,
    recipients,
    recipientList,
    currentRecipient: '',
    selectedRecipientIndex: -1,
  }
}

// Moves removed recipient to contacts.
function removeRecipient(state, action) {
  const { recipient, payerId } = action.data.recipient

  // Remove selected recipient.
  const recipients = state.recipients.filter(
    _recipient =>
      _recipient.recipient !== recipient ||
      (payerId && _recipient.payerId !== payerId)
  )

  const recipientList = recipients.map(_recipient => ({
    recipient: _recipient.recipient,
    payerId: _.get(_recipient, 'payerId', ''),
    value: '',
    showInvalid: false,
    isAmountFrozen: false,
    recipientDisplayDetails: _recipient.recipientDisplayDetails,
    ...(_recipient.networkId && { networkId: _recipient.networkId }),
  }))

  return {
    ...state,
    recipients,
    recipientList,
    selectedRecipientIndex: -1,
  }
}

function updateRecipientList(state, action) {
  return {
    ...state,
    recipientList: action.data.recipientList,
  }
}

// selected contacts get removed from `multiRecipient.contacts`
// so in order to reset everything we need to restore the contacts as well
function resetRecipients(state, action) {
  return {
    ...receivedContacts(initialState, action),
    searchedContacts: state.searchedContacts,
    hasFetchedContacts: state.hasFetchedContacts,
    recipients: [],
    recipientList: [],
  }
}

export default (state = initialState, action) => {
  switch (action.type) {
    case types.CONTACTS.RECEIVED_CONTACTS:
      return receivedContacts(state, action)
    case types.CONTACTS.RECEIVED_SEARCHED_CONTACTS:
      return receivedSearchedContacts(state, action)
    case types.RECIPIENTS.CHANGE_CURRENT_RECIPIENT_DISPLAY_NAME:
      return changeCurrentRecipientDisplayName(state, action)
    case types.RECIPIENTS.CHANGE_CURRENT_RECIPIENT:
      return changeCurrentRecipient(state, action)
    case types.RECIPIENTS.CHANGE_SELECTED_RECIPIENT_INDEX:
      return changeSelectedRecipientIndex(state, action)
    case types.RECIPIENTS.SELECT_CONTACT:
      return selectContact(state, action)
    case types.RECIPIENTS.REMOVE_RECIPIENT:
      return removeRecipient(state, action)
    case types.RECIPIENTS.UPDATE_RECIPIENT_LIST:
      return updateRecipientList(state, action)
    case types.RECIPIENTS.RESET_RECIPIENTS:
      return resetRecipients(state, action)
    case types.TRANSACTION.CANCELLED_TRANSACTION:
    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
      // Reset
      return {
        ...initialState,
        recipients: [],
        recipientList: [],
      }
    default:
      return state
  }
}
